import { ApolloError, gql, useMutation } from '@apollo/client';
// import { Roles } from '@modules/account/auth/auth.types';
import { Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, Grid, IconButton, Button as MuiButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchMembers from './SearchMembers';
// import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { Roles } from '@utils/roleUtils';
import { convertAssigneesToLegacyFormat, convertLegacyToAssigneeInput } from '../utils/assignee-utils';

const ASSIGN_JOB_OFFER_TO_USERS = gql`
    mutation assignJobOfferToUsers($jobOfferId: ID!, $assignees: [AssigneeInput!]!, $proId: ID) {
        assignJobOfferToUsers(jobOfferId: $jobOfferId, assignees: $assignees, proId: $proId) {
            _id
            assignees {
                _id
                assigneeType
                role
                assignedAt
                assigneeDetails {
                    ... on OrganizationUser {
                        _id
                        color
                        email
                        firstName
                        lastName
                        role
                        file {
                            _id
                            name
                            url
                        }
                    }
                    ... on Pro {
                        _id
                        email
                        bossFirstname
                        bossLastname
                        bossPhoto
                        commercialName
                        role
                    }
                }
            }
        }
    }
`;

export const AssignJobOfferDialog = ({ open, onClose, job, onSelected }) => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const [assignJobOfferToUsers, { loading: assigning }] = useMutation(ASSIGN_JOB_OFFER_TO_USERS, {
        onCompleted: data => {
            enqueueSnackbar('Offre d’emploi assignée avec succès', {
                variant: 'success',
            });
            onClose();
        },
        onError: error => {
            if (error instanceof ApolloError) {
                enqueueSnackbar(error.message.replace('Recovered : ', ''), {
                    variant: 'error',
                });
            }
        },
    });
    const [selectedUsers, setSelectedUsers] = useState([]);

    useEffect(() => {
        setSelectedUsers(convertAssigneesToLegacyFormat(job?.assignees));
    }, [job?.assignees]);
    const getRoleLabel = (role: string): string => {
        switch (role) {
            case Roles.ADMIN_PRO:
                return 'Admin';
            case Roles.PRO:
            case Roles.COMPANY:
                return 'Super Administrateur';
            default:
                return 'Membre';
        }
    };

    const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
        switch (role) {
            case Roles.ADMIN_PRO:
                return 'primary';
            case Roles.PRO:
            case Roles.COMPANY:
                return 'default';
            default:
                return 'secondary';
        }
    };
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <Stack gap={2} pt={3} px={3} pb={1}>
                <Stack gap={1}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                        Assigner un membre de l'équipe
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Veuillez sélectionner les personnes qui prendront part au process de recrutement pour l'offre{' '}
                        <strong>{job?.title}</strong>
                    </Typography>
                </Stack>
                <SearchMembers
                    value={selectedUsers}
                    proId={job?.proId}
                    placeholder={'Rechercher ou saisir un email...'}
                    onChange={value => {
                        setSelectedUsers(value);
                    }}
                />
                {selectedUsers?.length > 0 && (
                    <Typography variant="body2" fontWeight={500} sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        Équipe :
                    </Typography>
                )}
            </Stack>
            <DialogContent style={{ minHeight: 300 }}>
                {selectedUsers?.length === 0 && (
                    <Stack
                        gap={2}
                        p={2}
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Vous n'avez pas encore désigné de personne pour s'occuper de cette offre d'emploi.
                        </Typography>
                    </Stack>
                )}
                <DialogContentText>
                    <Grid container spacing={2}>
                        {selectedUsers?.map((option, index) => (
                            <Grid xs={12} item key={index}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" alignItems="center" gap={2}>
                                        <Avatar
                                            src={option.file?.url}
                                            sx={{
                                                bgcolor: option.color,
                                                width: 40,
                                                height: 40,
                                            }}>
                                            {option.firstName.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Typography variant="h6">
                                                    {option.firstName} {option.lastName}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={getRoleLabel(option?.role)}
                                                    color={getRoleColor(option?.role)}
                                                />
                                            </Stack>
                                            <Typography noWrap variant="body2">
                                                {option.email}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Box>
                                        <IconButton
                                            onClick={() => {
                                                const newSelected = selectedUsers.filter((_, i) => i !== index);
                                                setSelectedUsers(newSelected);
                                                // onChange(newSelected);
                                            }}>
                                            <i className="fi-br-cross" style={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContentText>
            </DialogContent>
            <DialogActions
                sx={{
                    gap: 2,
                }}>
                <MuiButton size="large" variant="text" color="secondary" onClick={onClose}>
                    Annuler
                </MuiButton>
                {!job?._id && (
                    <Button
                        onClick={() => {
                            onSelected(selectedUsers);
                            onClose();
                        }}>
                        Choisissez
                    </Button>
                )}
                {job?._id && (
                    <MuiButton
                        disabled={assigning}
                        size="large"
                        startIcon={assigning && <CircularProgress size={24} />}
                        onClick={() => {
                            // Convert selected users to the new assignee input format
                            const assigneeInputs = convertLegacyToAssigneeInput(selectedUsers);

                            assignJobOfferToUsers({
                                variables: {
                                    jobOfferId: job?._id,
                                    assignees: assigneeInputs,
                                    proId: job?.proId,
                                },
                                // update object fragment of JobOffer by the _id
                                updateQueries: {
                                    GetJobOffersPro: (prev, { mutationResult }) => {
                                        const newJobOffer = mutationResult.data.assignJobOfferToUsers;
                                        console.log(newJobOffer);
                                        const newJobOffers = prev.getJobOffersPro.docs.map(
                                            (job: { _id: string; assignees: any[] }) => {
                                                if (job?._id === newJobOffer._id) {
                                                    return {
                                                        ...job,
                                                        assignees: newJobOffer.assignees,
                                                    };
                                                }
                                                return job;
                                            },
                                        );
                                        return {
                                            GetJobOffersPro: {
                                                ...prev.getJobOffersPro,
                                                docs: newJobOffers,
                                            },
                                        };
                                    },
                                },
                            });
                        }}
                        color="primary"
                        autoFocus>
                        Assigner
                    </MuiButton>
                )}
            </DialogActions>
        </Dialog>
    );
};
